import type { Location } from 'history';
import { useEffect, useMemo } from 'react';

import { useHistory } from '@react-router';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const updateSearchParamValue = (paramName: string, paramValue: string | null, location: Location, replace: (nextLocation: Location) => void) => {
  // `location` is history's live location, so back-to-back updates in the same render cycle
  // and updates fired after a navigation both build on the current URL.
  const newSearch = new URLSearchParams(location.search);
  if (paramValue === null) {
    newSearch.delete(paramName);
  } else {
    newSearch.set(paramName, paramValue);
  }

  replace({
    ...location,
    search: newSearch.toString(),
  });
};

/**
 * Hook which stores and assists with managing state in a URL query parameter.
 *
 * @param searchParamName Name of the search param.
 * @param defaultValue Initial value of the search param if it's not present in the URL.
 */
export function useManagedSearchParam(
  searchParamName: string,
  defaultValue?: string | null,
  action: 'push' | 'replace' = 'replace',
): [queryParamValue: string | null, setQueryParamValue: (nextValue: string | null) => void] {
  const history = useHistory();
  const { location } = history;

  const queryParamValue = useMemo(() => {
    return new URLSearchParams(location.search).get(searchParamName);
  }, [location, searchParamName]);

  useEffect(() => {
    if (defaultValue !== undefined && !queryParamValue) {
      updateSearchParamValue(searchParamName, defaultValue, history.location, history.replace);
    }

    // This useEffect is intended to only run on first render to set initial search param value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setQueryParamValue = useEventCallback((nextValue: string | null) => {
    updateSearchParamValue(searchParamName, nextValue, history.location, action === 'replace' ? history.replace : history.push);
  });

  return [queryParamValue, setQueryParamValue];
}
